import React, { createContext, useEffect, useState, useContext } from "react";
import _ from "lodash";
import { getUser } from "../utils/graphql";
import { useAuth } from "./authContext";
import { gql, useSubscription } from "@apollo/client";

export const UserContext = createContext();

export const UserContextProvider = ({ children }) => {
  const { auth, setAuth } = useAuth();
  const [user, setUser] = useState({});

  const { data: subData, loading: subloading } = useSubscription(SUBSCRIPTION_SUB, {
    variables: { orgId: _.get(auth, "organization") },
  });

  const refreshUser = async (id) => {
    const userRes = await getUser(id);
    const userDoc = _.get(userRes, "data.user");
    setUser(userDoc);
  };

  useEffect(() => {
    if (!_.isNil(auth)) {
      refreshUser(auth.id);
    } else {
      setUser({});
    }
  }, [auth]);

  useEffect(() => {
    if (!_.isEmpty(user)) {
      setAuth((prev) => {
        // Only update if values are different to prevent infinite loop
        const needsUpdate = prev.isVerified !== user.isVerified || prev.organization !== user.organization;

        if (needsUpdate) {
          return {
            ...prev,
            isVerified: user.isVerified,
            organization: user.organization,
          };
        }

        // Return the previous state unchanged if no update is needed
        return prev;
      });
    }
  }, [user, setAuth]);

  useEffect(() => {
    if (!_.isEmpty(subData)) {
      const subscriptionData = _.get(subData, "subscriptionSub");
      const subscriptions = _.get(subscriptionData, "subscriptions") || [];
      const hasSubscriptionBypass = _.get(subscriptionData, "hasBypass") || false;
      const isSubscribed = !_.isEmpty(subscriptions);

      setAuth((prev) => {
        // Only update if values are different
        const needsUpdate = prev.isSubscribed !== isSubscribed || prev.hasSubscriptionBypass !== hasSubscriptionBypass;

        if (needsUpdate) {
          return {
            ...prev,
            isSubscribed,
            hasSubscriptionBypass,
          };
        }

        // Return the previous state unchanged if no update is needed
        return prev;
      });
    }
  }, [subData, setAuth]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};

const SUBSCRIPTION_SUB = gql`
  subscription ($orgId: ID) {
    subscriptionSub(orgId: $orgId) {
      orgId
      hasSubscriptionBypass
      subscriptions {
        quantity
        subscription
      }
    }
  }
`;
